import React, { useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import Swal from "sweetalert2";
import moment from "moment";
import { Icon } from "../../../../components/Component";
import { servicePutLeadReceptionStatus } from "services/users";
import { serviceGetProfile } from "services/settings";
import LeadReceptionModal from "./LeadReceptionModal";

// isLeadReceptionActive/leadReceptionReason/leadReceptionResumeAt/leadReceptionUpdatedAt
// always come from the API (GET api/auth/me on mount, or this component's own accept/pause
// calls) — never persisted to cookies, so a stale value can't survive a reload.
const LeadReceptionCard = ({ collapseSidebar, mouseEnter, contentMouseEnter }) => {
  const [cookies] = useCookies(["SIDikey", "role"]);
  const [leadReceptionModalOpen, setLeadReceptionModalOpen] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    serviceGetProfile(cookies.SIDikey).then((response) => {
      if (response?.data?.isLeadReceptionActive !== undefined) {
        setStatus(response.data);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const leadReceptionActive = status?.isLeadReceptionActive !== undefined ? status.isLeadReceptionActive : true;
  const leadReceptionUpdatedAt = status?.leadReceptionUpdatedAt;
  const leadReceptionReason = status?.leadReceptionReason;
  const leadReceptionResumeAt = status?.leadReceptionResumeAt;

  const activateLeadReception = async () => {
    const response = await servicePutLeadReceptionStatus(cookies.SIDikey, { active: true });
    if (response?.status >= 200 && response?.status < 300) {
      setStatus({ isLeadReceptionActive: true, leadReceptionUpdatedAt: response.data?.leadReceptionUpdatedAt });
    } else {
      Swal.fire("Lead Acceptable", "Failed to update status, please try again", "error");
    }
  };

  const handleSelectTerima = () => {
    if (!leadReceptionActive) {
      activateLeadReception();
    }
  };

  const handleSelectJeda = () => {
    if (leadReceptionActive) {
      setLeadReceptionModalOpen(true);
    }
  };

  const handleLeadReceptionDeactivated = ({ updatedAt, reason, resumeAt } = {}) => {
    setStatus({
      isLeadReceptionActive: false,
      leadReceptionUpdatedAt: updatedAt,
      leadReceptionReason: reason,
      leadReceptionResumeAt: resumeAt,
    });
  };

  const leadReceptionCaption = `Last updated: ${
    leadReceptionUpdatedAt ? moment.utc(leadReceptionUpdatedAt).local().format("DD/MM/YYYY HH:mm") : "-"
  }`;

  if (cookies.role?.name !== "SALES") {
    return null;
  }

  // Sidebar is fully collapsed (icon-only) and not being hovered — no room for the card.
  const isCollapsedAndNotHovered = collapseSidebar && !mouseEnter && !contentMouseEnter;
  if (isCollapsedAndNotHovered) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="border rounded p-3 mx-3 mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="lead-text">New Leads</span>
          <span className={`badge badge-dim badge-sm rounded-pill ${leadReceptionActive ? "badge-success" : "badge-warning"}`}>
            {leadReceptionActive ? "Accepting" : "Paused"}
          </span>
        </div>
        <div className="btn-group" role="group" style={{ display: "flex" }}>
          <button
            type="button"
            className={`btn btn-sm ${leadReceptionActive ? "btn-success" : "btn-outline-secondary"}`}
            style={{ flex: 1 }}
            onClick={handleSelectTerima}
          >
            <Icon name="check-circle-fill" className="mr-1"></Icon>
            Accept
          </button>
          <button
            type="button"
            className={`btn btn-sm ${!leadReceptionActive ? "btn-warning" : "btn-outline-secondary"}`}
            style={{ flex: 1 }}
            onClick={handleSelectJeda}
          >
            <Icon name="pause-circle-fill" className="mr-1"></Icon>
            Pause
          </button>
        </div>
        <p className="sub-text mb-0 mt-2">
          {leadReceptionActive
            ? "Auto-assigning new leads"
            : `Paused${leadReceptionReason ? `: ${leadReceptionReason}` : ""}`}
        </p>
        {!leadReceptionActive && leadReceptionResumeAt && (
          <p className="sub-text mb-0">
            Resumes at: {moment.utc(leadReceptionResumeAt).local().format("DD/MM/YYYY HH:mm")}
          </p>
        )}
        <p className="sub-text mb-0">{leadReceptionCaption}</p>
      </div>
      <LeadReceptionModal
        isOpen={leadReceptionModalOpen}
        onClose={() => setLeadReceptionModalOpen(false)}
        token={cookies.SIDikey}
        onSuccess={handleLeadReceptionDeactivated}
      />
    </React.Fragment>
  );
};

export default LeadReceptionCard;
